<template>
  <el-card id="dashboard-wordpress" shadow="hover">
    <div slot="header">
      <i class="mdi mdi-wordpress" /> Wordpress
    </div>

    <el-row :gutter="24">
      <el-col :xs="24" :sm="12" :md="8" :lg="8" :xl="6">
        <el-button
          type="default"
          class="open-dashboard"
          icon="mdi mdi-login"
          @click.prevent="sso"
        >
          Login to WP Admin
        </el-button>
      </el-col>

      <el-col :xs="24" :sm="12" :md="16" :lg="16" :xl="18">
        <el-collapse accordion>
          <el-collapse-item name="performance">
            <template slot="title">
              Performance Cache
              <el-tag v-if="form.proxy_performance_cache === '1'" type="success" size="small">ON</el-tag>
              <el-tag v-else type="warning" size="small">OFF</el-tag>
            </template>

            <el-switch
              v-model="form.proxy_performance_cache"
              active-text="Enable"
              active-value="1"
              inactive-value="0"
              @change="changeDebounce"
            />

            <p>
              <small>
                The cache is managed by runstack in pair with the Wordpress Core when the content changes.
                You benefit from a caching system that no plugin can match.
              </small>
            </p>

            <el-button
              size="small"
              type="danger"
              :disabled="form.proxy_performance_cache !== '1'"
              @click.prevent="purge"
            >
              Purge everything
            </el-button>
          </el-collapse-item>

          <el-collapse-item name="hacking">
            <template slot="title">
              Hacking Prevention
              <el-tag v-if="form.wp_hack_prevent === '1'" type="success" size="small">ON</el-tag>
              <el-tag v-else type="warning" size="small">OFF</el-tag>
            </template>

            <el-switch
              v-model="form.wp_hack_prevent"
              active-text="Enable"
              active-value="1"
              inactive-value="0"
              @change="changeDebounce"
            />

            <p>
              <small>
                Access to Wordpress admin and login is denied for all users, except runstack client (based on the ip address that logged into runstack).
                This mitigates the brute-force attempts and hacking of the Core of Wordpress, but <u>not</u> of the vulnerability of a third-party plugin.
              </small>
            </p>
          </el-collapse-item>

          <el-collapse-item title="Update URLs" name="updateUrls">
            <el-alert
              type="warning"
              title="We recommend you to save your site before !"
              show-icon
              :closable="false"
            />

            <p>
              <small>After moving a site, this tool allows you to repair content, links and custom fields.</small>
            </p>

            <el-form ref="srdb" size="mini" :model="form" :rules="rules">
              <el-form-item label="Old Site Address" prop="oldUrl">
                <el-input v-model="form.oldUrl" placeholder="Ex: http://www.oldurl.com" />
              </el-form-item>

              <el-form-item label="New Site Address" prop="newUrl">
                <el-input v-model="form.newUrl" placeholder="Ex: http://www.newurl.com" />
              </el-form-item>

              <el-button
                size="small"
                type="primary"
                :loading="updatingUrls"
                @click.prevent="updateUrls"
              >
                Search and Replace
              </el-button>
            </el-form>
          </el-collapse-item>

          <el-collapse-item title="Import from another Wordpress" name="importer">
            <p>
              <small>Importer gives you the ability to copy a site from another location.</small>
            </p>

            <el-form ref="search" size="mini" :model="form" :rules="rules">
              <el-form-item label="Wordpress Homepage" prop="location" :error="error.location">
                <el-input v-model="form.location" placeholder="Ex: http://www.wordpress.org" />
              </el-form-item>

              <el-button
                size="small"
                type="info"
                :loading="importing"
                @click="checkLocation"
              >
                Import data
              </el-button>

              <el-dialog class="importer" title="Log In" :visible.sync="open.importer">
                <img src="../../../assets/wordpress-wine.png" />

                <el-alert
                  type="warning"
                  title="your runstack wordpress will be overwritten !"
                  effect="dark"
                  :closable="false"
                  show-icon
                />

                <div class="login">
                  <el-form-item label="Administrator Username" prop="username">
                    <el-input v-model="form.username" />
                  </el-form-item>
                  <el-form-item label="Administrator Password" prop="password">
                    <el-input v-model="form.password" type="password" />
                  </el-form-item>

                  <el-button
                    size="small"
                    type="primary"
                    :loading="importing"
                    @click="importData"
                  >
                    Import data
                  </el-button>
                </div>
              </el-dialog>
            </el-form>
          </el-collapse-item>
        </el-collapse>
      </el-col>
    </el-row>
  </el-card>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import config from '../../../mixins/config'

export default {
  mixins: [config],
  props: {
    application: Object
  },
  data () {
    return {
      open: {
        importer: false
      },
      error: {
        location: null
      },
      form: {
        oldUrl: null,
        newUrl: null,
        location: null,
        username: null,
        password: null
      },
      rules: {
        oldUrl: [
          { required: true, message: 'Please input the search field.', trigger: 'blur' }
        ],
        newUrl: [
          { required: true, message: 'Please input the replace field.', trigger: 'blur' }
        ],
        location: [
          { required: true, message: 'Please input the wordpress location field.', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    updatingUrls () {
      return this.tasks.find((t) => t.applicationId === this.application.id && t.scope === 'search-replace')
    },
    importing () {
      return this.tasks.find((t) => t.applicationId === this.application.id && t.scope === 'importer')
    },
    ...mapState(['roleId', 'token', 'tasks'])
  },
  methods: {
    sso () {
      const redirect = axios.defaults.baseURL + 'engine/wordpress/' + this.application.id + '/sso?' +
      'role=' + this.roleId + '&token=' + this.token
      window.open(redirect, '_blank')
    },
    purge () {
      this.$store.dispatch('purgeApplication', this.application)
        .then(() => this.$notify({
          title: 'Cleaning Application',
          message: 'Please allow up to 30 seconds for complete.',
          type: 'success'
        })).catch(() => {})
    },
    updateUrls () {
      this.$refs.srdb.validate((valid) => {
        if (valid) {
          this.$confirm('You will update the wordpress. Continue?', 'Warning', {
            confirmButtonText: 'Search and Replace',
            cancelButtonText: 'Cancel',
            type: 'warning'
          }).then(() => this.$store.dispatch('wpSearchReplace', { ...this.form, id: this.application.id, instanceId: this.application.instanceId }))
            .then(() => this.$notify({
              title: 'Updating urls...',
              message: 'it may take some time depending on the database.',
              type: 'success'
            }))
            .then(() => {
              this.form.oldUrl = null
              this.form.newUrl = null
            })
            .catch(() => {})
        }
      })
    },
    checkLocation () {
      this.$refs.search.validate((valid) => {
        if (valid) {
          this.$store.dispatch('wpCheckLocation', this.form).then(() => {
            this.open.importer = true
          }).catch(() => {
            this.error.location = 'This url doesn\'t seem to be a Wordpress'
          })
        }
      })
    },
    importData () {
    }
  }
}
</script>

<style lang="scss" scoped>
  .open-dashboard {
    width: 100%;
  }
  .el-collapse {
    margin: -13px -12px 12px;

    p small {
      font-size: 85%;
      display: block;
    }

    .el-switch {
      display: block;
    }
  }
</style>

<style lang="scss">
  @import "../../../theme";

  #dashboard-wordpress {
    .el-collapse-item > div > div {
      padding-left: 12px;
      padding-right: 10px;

      .el-tag {
        position: absolute;
        right: 46px;
      }
    }
    .importer {
      img {
        max-width: 150px;
        margin: -50px auto 0;
        display: block;
      }
      .el-dialog {
        width: 410px;
        background: #f1f1f1;
      }
      .login {
        background: #fff;
        border: 1px solid #ccd0d4;
        box-shadow: 0 1px 3px rgba(0,0,0,.04);
        width: 320px;
        margin: 0 auto;
        padding: 26px 24px 46px;

        .el-input {
          background: #fbfbfb;
          border-radius: 4px;
          border: 1px solid #7e8993;
        }
      }
    }
  }
</style>
